import { isNil } from 'lodash/fp';
import { type Dispatch, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { JourneyStage } from '../FinderApplicationPublicAccessEntrypoint/types';
import { type Action, type State } from '../StandardApplicationEntrypoint/Journey/reducer';
import { type ConfiguratorApplicationState } from './Journey/JourneyController';

const useConfiguratorLayout = (
    dispatch: Dispatch<Action<ConfiguratorApplicationState>>,
    state: State<ConfiguratorApplicationState>
): {
    title: string;
    onBack: () => void;
    shouldShowBackIcon?: boolean;
    /**
     * This is used to determine whether to wrap the content page with JourneyLayout. default is true.
     */
    useJourneyLayout?: boolean;
} => {
    const { t } = useTranslation([
        'customerDetails',
        'journey',
        'paymentDetails',
        'confirmRemoteFlow',
        'otpPage',
        'appointmentPage',
    ]);
    const { application, stage: journeyStage } = state;

    const navigate = useNavigate();

    const shouldShowBackIcon = !application.withCustomerDevice;
    const isApplyingFromDetails = useMemo(
        () =>
            (application.configuration.withFinancing && application.configuration.requestForFinancing) ||
            !isNil(application.draftFlow.isApplyingForFinanceCompleted) ||
            !isNil(application.draftFlow.isApplyingForInsuranceCompleted),
        [
            application.configuration.requestForFinancing,
            application.configuration.withFinancing,
            application.draftFlow.isApplyingForFinanceCompleted,
            application.draftFlow.isApplyingForInsuranceCompleted,
        ]
    );

    const vehicleId =
        application.__typename === 'ConfiguratorApplication'
            ? application.configurator.modelConfigurator.urlIdentifier
            : undefined;

    const useJourneyLayout = true;

    switch (journeyStage) {
        case JourneyStage.ApplicantKYC:
            return {
                title: t('customerDetails:title'),
                onBack:
                    shouldShowBackIcon && vehicleId ? () => navigate(`../details/${vehicleId}`) : () => navigate(-1),
                shouldShowBackIcon: shouldShowBackIcon && !isApplyingFromDetails,
                useJourneyLayout,
            };

        case JourneyStage.GuarantorKYC:
            return {
                title: t('customerDetails:title'),
                onBack: () => navigate('..'),
                shouldShowBackIcon,
                useJourneyLayout,
            };

        case JourneyStage.Deposit:
            return {
                title: t('paymentDetails:title'),
                onBack: () => dispatch({ type: 'goTo', stage: JourneyStage.ApplicantKYC }),
                shouldShowBackIcon,
                useJourneyLayout,
            };

        case JourneyStage.ConfirmEmailSend:
            return {
                title: t('confirmRemoteFlow:title'),
                onBack: () => {},
                shouldShowBackIcon,
                useJourneyLayout: false,
            };

        case JourneyStage.Otp:
            return {
                title: t('otpPage:title'),
                onBack: () => navigate('..'),
                shouldShowBackIcon,
                useJourneyLayout,
            };

        default:
            return {
                title: '',
                onBack: () => {},
                shouldShowBackIcon,
                useJourneyLayout,
            };
    }
};

export default useConfiguratorLayout;
